import { TabBar } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useDeputateLessons } from '../../../../components/Lessons/hooks/useDeputateLessons';
import { DeputateLessonsTable } from '../../../../components/Lessons/Tables/DeputateLessonsTable';
import { Page } from '../../../../components/Page/Page';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { LessonTabsType } from './LessonsPage';

export const LessonOverviewSubpage = observer(() => {
  const { t } = useTranslation();
  const schoolYear = useSelectedSchoolYear();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();

  const { lessons: lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const assignedData = lessonsData.filter((l) => l.teachers.length > 0 && !l.isSubjectContainerLesson) ?? [];
  const unassignedData = lessonsData.filter((l) => l.teachers.length === 0 && !l.isSubjectContainerLesson) ?? [];
  const subjectContainerLessons = lessonsData.filter((l) => l.isSubjectContainerLesson) ?? [];
  const defaultLessons = lessonsData.filter((l) => !l.isSubjectContainerLesson) ?? [];

  const subjectContainerLessonsData =
    subjectContainerLessons.sort((scl1, scl2) => {
      const class1 = scl1.classes[0];
      const class2 = scl2.classes[0];
      return (
        scl1.subject.localeCompare(scl2.subject) ||
        (class1?.grade ?? 0) - (class2?.grade ?? 0) ||
        (class1?.gradeGroup?.name ?? '').localeCompare(class2?.gradeGroup?.name ?? '')
      );
    }) ?? [];

  const tabs: LessonTabsType[] = [
    {
      trigger: t('lesson.all'),
      id: 'all',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='all'
          configKey='lessons-table-overview-all'
          lessonsData={defaultLessons}
          showTransferCurricula
        />
      ),
    },
    {
      trigger: t('lesson.assigned'),
      id: 'assigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='assigned'
          configKey='lessons-table-overview-assigned'
          lessonsData={assignedData}
          showTransferCurricula
        />
      ),
    },
    {
      trigger: t('lesson.unassigned'),
      id: 'unassigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='unassigned'
          configKey='lessons-table-overview-unassigned'
          lessonsData={unassignedData}
          showTransferCurricula
        />
      ),
    },
    {
      trigger: t('teachingBlockSubjectContainer.title', {
        count: subjectContainerLessons.length,
      }),
      id: 'subjectContainerLessons',
      content: (
        <>
          <DeputateLessonsTable
            lessonTabFilterType='subjectContainerLessons'
            configKey='lessons-table-overview-subjectContainerLessons'
            lessonsData={subjectContainerLessonsData}
          />
        </>
      ),
    },
  ];

  return (
    <Page isForbidden={!permissions?.canReadDeputate()} hasOutlet>
      <TabBar tabs={tabs} />
    </Page>
  );
});
